import { useEffect, useState, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { computeLevel } from "@shared/xp";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Settings, ShieldCheck, Flame, Zap, Star, Rss, Users, X, UserPlus, Camera, Pencil, Check, MessageSquarePlus,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ProfilePhotoViewer } from "@/components/ProfilePhotoViewer";
import { getDisplayName } from "@/lib/userDisplay";
import { FeedbackModal } from "@/components/FeedbackModal";
import { StaffBadge } from "@/components/StaffBadge";
import { LearnerProfile } from "@/components/profile/LearnerProfile";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { ProfileHero, ProfileStatTiles, SubjectMastery, BadgesSection, ConnectionsCard, PodiumRibbon, Initials } from "@/components/profile/ProfileSections";

interface ProfileData {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    grade: number;
    school: string;
    profilePhotoUrl?: string;
    bio?: string | null;
    currentStreak: number;
    bestStreak: number;
    createdAt: string;
    role?: string;
  };
  stats: {
    totalPoints: number;
    totalCorrect: number;
    totalQuestions: number;
    accuracy: number;
    currentLevel: number;
    currentXP: number;
    subjectBreakdown: Array<{ subject: string; points: number; fatigue: number }>;
  };
  rank: number | null;
  totalPoints: number;
  followerCount: number;
  followingCount: number;
  achievements: Array<{ type: string; earnedAt: string }>;
}

interface ConnectionUser {
  id: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  grade?: number;
  school?: string;
  currentLevel?: number;
}

interface DiscoverUser extends ConnectionUser {}

interface FeedReaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
}

interface FeedItem {
  id: string;
  userId: string;
  activityType: string;
  payload: any;
  createdAt: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  reactions?: FeedReaction[];
}

const ACTIVITY_LABELS: Record<string, string> = {
  badge_earned: "earned a badge",
  challenge_won: "won a challenge",
  challenge_lost: "completed a challenge",
  streak_milestone: "hit a streak milestone",
  level_up: "levelled up",
  correct_milestone: "hit a correct answers milestone",
  new_follower: "gained a new follower",
};

export default function MyProfile() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { user: currentUser } = useAuth();
  // /profile?tab=learner opens the learning tab directly (the Courses page links here).
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(search).get("tab") === "learner" ? "learner" : "profile"
  );

  useEffect(() => {
    const tab = new URLSearchParams(search).get("tab");
    if (tab === "learner" || tab === "profile") setActiveTab(tab);
  }, [search]);

  const [viewingPhoto, setViewingPhoto] = useState<{ url: string; name: string } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());
  const [editingBio, setEditingBio] = useState(false);
  const [bioValue, setBioValue] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handlePhotoUpload = async (file: File) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await fetch("/api/profile/photo/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to upload photo");
      }
      const result = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profile", currentUser?.id] });
      toast({
        title: result.bonusAwarded ? "Photo updated + 25 XP Bonus!" : "Photo updated",
        description: result.bonusAwarded ? "You earned 25 XP for your first profile photo!" : undefined,
      });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const { data: profile, isLoading, error: profileError } = useQuery<ProfileData>({
    queryKey: ["/api/profile", currentUser?.id],
    enabled: !!currentUser?.id,
  });

  const { data: connections } = useQuery<{ followers: ConnectionUser[]; following: ConnectionUser[] }>({
    queryKey: ["/api/social/connections", currentUser?.id],
    enabled: !!currentUser?.id,
  });

  const { data: pending = [] } = useQuery<any[]>({
    queryKey: ["/api/social/pending"],
  });

  const { data: suggestions = [] } = useQuery<DiscoverUser[]>({
    queryKey: ["/api/social/discover"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/social/discover");
        return res.json();
      } catch { return []; }
    },
  });

  const { data: feedData } = useQuery<{ items: FeedItem[]; hasMore: boolean }>({
    queryKey: ["/api/social/feed"],
  });

  const bioMutation = useMutation({
    mutationFn: async (bio: string) => {
      const res = await apiRequest("PATCH", "/api/user/bio", { bio });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile", currentUser?.id] });
      setBioValue(data.bio ?? "");
      setEditingBio(false);
      toast({ title: "Bio saved" });
    },
    onError: () => toast({ title: "Failed to save bio", variant: "destructive" }),
  });

  const suggestFollowMutation = useMutation({
    mutationFn: async (targetId: string) => {
      await apiRequest("POST", `/api/social/follow/${targetId}`);
    },
    onSuccess: (_data, targetId) => {
      setSentRequests(prev => new Set([...prev, targetId]));
      queryClient.invalidateQueries({ queryKey: ["/api/social/discover"] });
    },
  });

  if (isLoading) {
    return <div className="container mx-auto p-4 max-w-5xl"><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div></div>;
  }
  if (profileError || !profile) {
    return (
      <div className="container mx-auto p-4 max-w-5xl">
        <StudentEmptyState illustration="owl-sleepy" title="Owlbert could not load your profile." hint={profileError ? String((profileError as any)?.message ?? "Try again in a moment.") : "Try again in a moment."} action={<Button onClick={() => window.location.reload()}>Reload</Button>} />
      </div>
    );
  }

  const followerIds = new Set((connections?.followers ?? []).map(f => f.id));
  const visibleSuggestions = suggestions.filter(u => !dismissedSuggestions.has(u.id)).slice(0, 4);
  const recentFeed = (feedData?.items ?? []).slice(0, 5);
  const pendingCount = pending.length;
  const level = computeLevel(profile.stats?.totalPoints ?? 0).currentLevel;
  const joined = profile.user.createdAt ? new Date(profile.user.createdAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : null;
  const me = profile.user;

  const bioBlock = editingBio ? (
    <div className="space-y-1.5 max-w-xl mx-auto md:mx-0">
      <Textarea value={bioValue} onChange={e => setBioValue(e.target.value.slice(0, 160))} placeholder="Tell classmates about yourself..." className="text-sm resize-none h-16 bg-white text-foreground" autoFocus />
      <div className="flex items-center gap-2">
        <Button size="sm" className="h-7 text-xs bg-white text-primary hover:bg-white/90" onClick={() => bioMutation.mutate(bioValue)} disabled={bioMutation.isPending}><Check className="h-3 w-3 mr-1" />Save</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-primary-foreground hover:bg-white/15" onClick={() => { setEditingBio(false); setBioValue(me.bio ?? ""); }}>Cancel</Button>
        <span className="text-xs text-primary-foreground/70 ml-auto tabular-nums">{bioValue.length}/160</span>
      </div>
    </div>
  ) : (
    <button type="button" className="group inline-flex items-start gap-1.5 text-left max-w-xl" onClick={() => { setBioValue(me.bio ?? ""); setEditingBio(true); }} data-testid="button-edit-bio">
      {me.bio ? <p className="text-sm text-primary-foreground/90 leading-snug">{me.bio}</p> : <p className="text-sm text-primary-foreground/60 italic">Add a short bio so classmates know you.</p>}
      <Pencil className="h-3.5 w-3.5 text-primary-foreground/60 group-hover:text-primary-foreground mt-0.5 shrink-0 transition-colors" />
    </button>
  );

  return (
    <>
    <div className="container mx-auto p-4 md:p-6 max-w-5xl space-y-4 pb-24 md:pb-8">
      <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const file = e.target.files?.[0]; if (file) handlePhotoUpload(file); e.target.value = ""; }} />

      <ProfileHero
        user={me} rank={profile.rank} totalPoints={profile.totalPoints} level={level}
        followerCount={profile.followerCount} followingCount={profile.followingCount} pendingCount={pendingCount} joinedLabel={joined}
        onCounts={() => document.getElementById("connections-tabs")?.scrollIntoView({ behavior: "smooth" })}
        bio={bioBlock}
        avatar={
          <div className="relative group">
            <button type="button" className="rounded-full ring-4 ring-white/40 cursor-pointer" onClick={() => me.profilePhotoUrl && !uploadingPhoto ? setViewingPhoto({ url: me.profilePhotoUrl, name: `${me.firstName} ${me.lastName}` }) : photoInputRef.current?.click()} aria-label={me.profilePhotoUrl ? "View photo" : "Add a photo"}>
              <Initials user={me} onPurple className="h-24 w-24 md:h-28 md:w-28 text-3xl" />
            </button>
            <button type="button" className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white text-primary flex items-center justify-center shadow-sm hover:bg-white/90" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} title="Change photo" data-testid="button-change-photo">
              {uploadingPhoto ? <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
          </div>
        }
        actions={
          <>
            <Button size="sm" className="rounded-lg bg-white text-primary hover:bg-white/90" onClick={() => navigate("/settings")} data-testid="button-edit-settings"><Settings className="h-4 w-4 mr-1.5" />Edit settings</Button>
            <Button size="sm" variant="secondary" className="rounded-lg bg-white/20 hover:bg-white/25 text-primary-foreground" onClick={() => setFeedbackOpen(true)} title="Suggest a feature or report a problem"><MessageSquarePlus className="h-4 w-4 mr-1.5" />Feedback</Button>
          </>
        }
      />

      {!me.profilePhotoUrl && (
        <div className="rounded-xl border border-primary/25 bg-primary/10 px-4 py-3 flex items-center gap-3" data-testid="nudge-photo">
          <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 -rotate-2 shadow-sm"><Camera className="h-5 w-5" /></div>
          <p className="text-sm flex-1">Add a profile photo so classmates can recognise you. First photo earns 25 XP.</p>
          <Button size="sm" className="rounded-lg" onClick={() => photoInputRef.current?.click()}>Add photo</Button>
        </div>
      )}
      <PodiumRibbon rank={profile.rank} grade={me.grade} />

      <div className="flex gap-1 p-1 rounded-full bg-muted w-full sm:w-auto sm:inline-flex">
        {([["profile", "Profile"], ["learner", "My learning"]] as const).map(([v, label]) => (
          <button key={v} type="button" onClick={() => setActiveTab(v)} className={`flex-1 sm:flex-none rounded-full px-5 py-2 text-sm font-semibold transition-colors ${activeTab === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-primary/10"}`} data-testid={v === "profile" ? "tab-my-profile" : "tab-my-learning"}>{label}</button>
        ))}
      </div>

      {activeTab === "profile" ? (
        <div className="space-y-4">
          <ProfileStatTiles accuracy={profile.stats.accuracy} totalQuestions={profile.stats.totalQuestions} totalCorrect={profile.stats.totalCorrect} streak={me.currentStreak ?? 0} badges={profile.achievements?.length ?? 0} xp={profile.stats.totalPoints} />
          <SubjectMastery subjects={profile.stats?.subjectBreakdown ?? []} ownerFirstName={me.firstName} />
          <BadgesSection achievements={profile.achievements ?? []} isSelf />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ConnectionsCard id="connections-tabs" isSelf following={connections?.following ?? []} followers={connections?.followers ?? []} followerIds={followerIds} pendingCount={pendingCount}
              defaultTab={pendingCount > 0 ? "followers" : "following"} onOpen={u => navigate(`/user/${u.id}`)} onFindPeople={() => navigate("/social")} onReviewPending={() => navigate("/social")} />

            <div className="space-y-4">
              {visibleSuggestions.length > 0 && (
                <section className="rounded-xl border border-border bg-card p-4 md:p-5 space-y-3" data-testid="profile-suggestions">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-base md:text-lg font-semibold inline-flex items-center gap-2"><Users className="w-5 h-5 text-primary" />People you may know</h2>
                    <Button variant="ghost" size="sm" className="text-primary" onClick={() => navigate("/social")}>View all</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {visibleSuggestions.map(u => (
                      <div key={u.id} className="rounded-xl border border-border bg-primary/5 p-3 flex flex-col items-center gap-2 relative">
                        <button type="button" className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground p-1" onClick={() => setDismissedSuggestions(prev => new Set([...prev, u.id]))} aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => navigate(`/user/${u.id}`)}><Initials user={u} className="h-12 w-12" /></button>
                        <div className="text-center min-w-0 w-full">
                          <p className="text-sm font-semibold truncate">{u.firstName} {u.lastName}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.school || `Grade ${u.grade}`}</p>
                        </div>
                        <Button size="sm" className="w-full h-8 text-xs rounded-lg" variant={sentRequests.has(u.id) ? "outline" : "default"} onClick={() => !sentRequests.has(u.id) && suggestFollowMutation.mutate(u.id)} disabled={sentRequests.has(u.id)}>
                          {sentRequests.has(u.id) ? "Requested" : <><UserPlus className="h-3.5 w-3.5 mr-1" />Follow</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className="rounded-xl border border-border bg-card p-4 md:p-5 space-y-3" data-testid="profile-feed">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-base md:text-lg font-semibold inline-flex items-center gap-2"><Rss className="w-5 h-5 text-primary" />Friend activity</h2>
                  {recentFeed.length > 0 && <Button variant="ghost" size="sm" className="text-primary" onClick={() => navigate("/feed")}>View all</Button>}
                </div>
                {recentFeed.length === 0 ? (
                  <StudentEmptyState illustration="owl-search" size={90} title="Nothing here yet." hint="Follow classmates to see their badges, streaks and level-ups." action={<Button size="sm" onClick={() => navigate("/social")}>Find people</Button>} />
                ) : (
                  <ul className="divide-y divide-border">
                    {recentFeed.map(item => {
                      const myReacted = (item.reactions ?? []).find(r => r.hasReacted)?.emoji ?? null;
                      const reactionCounts = (item.reactions ?? []).filter(r => r.count > 0);
                      return (
                        <li key={item.id} className="flex items-start gap-3 py-2.5">
                          <button type="button" onClick={() => navigate(`/user/${item.userId}`)}><Initials user={item} className="h-9 w-9" /></button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm leading-snug"><span className="font-semibold">{item.firstName}</span> {ACTIVITY_LABELS[item.activityType] ?? item.activityType}{item.payload?.badgeName ? `: ${item.payload.badgeName}` : ""}</p>
                            <p className="text-xs text-muted-foreground">{new Date(item.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                            {reactionCounts.length > 0 && (
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                {reactionCounts.map(r => (
                                  <button key={r.emoji} type="button"
                                    onClick={() => { (myReacted === r.emoji ? apiRequest("DELETE", `/api/social/feed/${item.id}/react`) : apiRequest("POST", `/api/social/feed/${item.id}/react`, { emoji: r.emoji })).then(() => queryClient.invalidateQueries({ queryKey: ["/api/social/feed"] })); }}
                                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${myReacted === r.emoji ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border bg-muted/40 text-muted-foreground hover:border-primary/50"}`}>
                                    <span>{r.emoji}</span><span>{r.count}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </div>
      ) : (
        <LearnerProfile />
      )}
    </div>

    <ProfilePhotoViewer
      url={viewingPhoto?.url ?? null}
      name={viewingPhoto?.name ?? ""}
      onClose={() => setViewingPhoto(null)}
    />
    <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}
